import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { 
  Box, 
  IconButton, 
  Typography, 
  CircularProgress
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useMsal } from '@azure/msal-react';
import { getConversations, Conversation } from '../Services/ChatHistoryService';
import './ChatHistory.css';
interface ChatHistoryProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onConversationSelect?: (conversation: Conversation) => void;
  onNewChat?: () => void;
  currentConversationId?: number;
}

export interface ChatHistoryRef {
  refreshConversations: () => Promise<void>;
}

const ChatHistory = forwardRef<ChatHistoryRef, ChatHistoryProps>(({ 
  isCollapsed, 
  onToggleCollapse, 
  onConversationSelect,
  onNewChat,
  currentConversationId
}, ref) => {
  const { instance, accounts } = useMsal();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Fetch conversations when component mounts or when account changes
  useEffect(() => {
    if (!accounts[0]) return;

    const fetchConversations = async () => {
      setLoading(true);
      setError('');
      
      try {
        const data = await getConversations(instance, accounts[0]);
        setConversations(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching conversations:', err);
        setError('Failed to load chat history');
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, [instance, accounts]);

  // Format conversation title
  const formatConversationTitle = (conversation: Conversation): string => {
    const now = new Date();
    
    // Parse the UTC date string and convert to local time
    // The backend sends dates in UTC format, so we need to parse them correctly
    let conversationDate: Date;
    try {
      // If the date string doesn't end with 'Z', add it to ensure UTC parsing
      const dateString = conversation.createdAt.endsWith('Z') 
        ? conversation.createdAt 
        : conversation.createdAt + 'Z';
      conversationDate = new Date(dateString);
      
      // Check if the date is valid
      if (isNaN(conversationDate.getTime())) {
        console.error('Invalid date parsed:', conversation.createdAt, 'for conversation:', conversation);
        return `${conversation.companyName} - Invalid Date`;
      }
    } catch (error) {
      console.error('Error parsing date:', conversation.createdAt, 'for conversation:', conversation, error);
      return `${conversation.companyName} - Invalid Date`;
    }
    
    // Check if it's today
    const isToday = now.toDateString() === conversationDate.toDateString();
    
    // Check if it's yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = yesterday.toDateString() === conversationDate.toDateString();
    
    let dateDisplay: string;
    if (isToday) {
      dateDisplay = 'Today';
    } else if (isYesterday) {
      dateDisplay = 'Yesterday';
    } else {
      // Show full date for older conversations
      dateDisplay = conversationDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    
    // Format time in local timezone with 12-hour format
    const time = conversationDate.toLocaleTimeString('en-US', { 
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    return `${conversation.companyName} - ${dateDisplay} ${time}`;
  };



  // Handle conversation selection
  const handleConversationClick = (conversation: Conversation) => {
    if (onConversationSelect) {
      onConversationSelect(conversation);
    }
  };

  // Refresh conversations
  const refreshConversations = async () => {
    if (!accounts[0]) return;
    
    setLoading(true);
    setError('');
    
    try {
      const data = await getConversations(instance, accounts[0]);
      setConversations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error refreshing conversations:', err);
      setError('Failed to refresh chat history');
    } finally {
      setLoading(false);
    }
  };

  // Expose refreshConversations to parent component
  useImperativeHandle(ref, () => ({
    refreshConversations
  }));

  const list = Array.isArray(conversations) ? conversations : [];

  return (
    <Box className={`chat-history${isCollapsed ? ' collapsed' : ''}`}>
      <IconButton onClick={onToggleCollapse} className="chat-history__toggle">
        {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </IconButton>
      
      {!isCollapsed && (
        <Box className="chat-history__content">
          <Box className="chat-history__header">
            <Typography variant="h6">
              Chat History
            </Typography>
            <Box className="chat-history__actions">
              <IconButton 
                size="small" 
                onClick={onNewChat}
                title="New Chat"
              >
                +
              </IconButton>
              <IconButton 
                size="small" 
                onClick={refreshConversations}
                disabled={loading}
                title="Refresh"
              >
                {loading ? <CircularProgress size={16} /> : '↻'}
              </IconButton>
            </Box>
          </Box>
          
          {error && (
            <Typography 
              color="error" 
              variant="caption" 
              className="chat-history__error"
            >
              {error}
            </Typography>
          )}
          
          <Box className="chat-history__scroll">
            {loading && list.length === 0 ? (
              <Box className="chat-history__loading">
                <CircularProgress size={24} />
              </Box>
            ) : list.length === 0 ? (
              <Typography 
                variant="body2" 
                color="text.secondary"
                className="chat-history__empty"
              >
                No conversations yet
              </Typography>
            ) : (
              <Box className="chat-history__list">
                {list.map((conversation) => (
                  <React.Fragment key={conversation.id}>
                    <Box
                      onClick={() => handleConversationClick(conversation)}
                      className={`chat-history__item${currentConversationId === conversation.id ? ' active' : ''}`}
                    >
                      <Typography 
                        variant="body2" 
                        className="chat-history__itemTitle"
                      >
                        {formatConversationTitle(conversation)}
                      </Typography>
                    </Box>
                  </React.Fragment>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default ChatHistory;
